import clsx from 'clsx';
import { forwardRef, useEffect, useReducer, useRef, useState } from 'react';

import { chipGroupCSS } from './ChipGroup.sty.js';
import { ChipGroupContext } from './contexts/ChipGroupContext.js';
import { InternalChipGroupContext } from './contexts/InternalChipGroupContext.js';
import { Control } from './Control.js';
import { HiddenCount } from './HiddenCount.js';
import { chipGroupReducer } from './state/chip-group-state-reducer.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useContainerSize } from '../../core/hooks/useContainerSize.js';
import { useControllableState } from '../../core/hooks/useControllableState.js';
import { useId } from '../../core/hooks/useId.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { WithChildren } from '../../core/types/with-children.js';
import { Flex } from '../../layouts/flex/Flex.js';
import type { ChipOwnProps } from '../chip/types/chip.js';

const COMPONENT_NAME = 'ChipGroup';

/**
 * Accepted base properties for ChipGroup.
 * @public
 */
export interface ChipGroupBaseProps
  extends StylingProps,
    DataTestId,
    MaskingProps,
    AriaLabelingProps,
    BehaviorTrackingProps {
  /**
   * Whether the chip group is disabled.
   * If set to true, all the chips inside the group are disabled, unless explicitly overridden.
   * @defaultValue false
   */
  disabled?: boolean;

  /**
   * The size of the spacing and chips in the group.
   * All the chips match the size of the group, but can be overridden individually.
   * @defaultValue default
   */
  size?: ChipOwnProps['size'];

  /**
   * Callback triggered when the expanded state changes.
   */
  onExpandedChange?: (state: boolean) => void;

  /**
   * Maximum number of chips rendered before the "Show more" control.
   *
   * If set to 'auto', the chip group automatically determines
   * how many chips can fit based on the available width.
   * If a number is provided, it limits the visible chips to that number
   * when the group is collapsed, regardless of available space.
   * @defaultValue 'auto'
   */
  maxVisibleChips?: 'auto' | number;
}

/**
 * Properties for the controlled variant of ChipGroup.
 * @public
 */
export interface ChipGroupControlledProps {
  /** Whether the chip group is expanded. */
  expanded?: boolean;
  /** Whether the chip group is expanded by default. */
  defaultExpanded?: never;
}

/**
 * Properties for the uncontrolled variant of ChipGroup.
 * @public
 */
export interface ChipGroupUncontrolledProps {
  /** Whether the chip group is expanded. */
  expanded?: never;
  /** Whether the chip group is expanded by default. */
  defaultExpanded?: boolean;
}

/**
 * Properties for the ChipGroup component.
 * @public
 */
export type ChipGroupProps = ChipGroupBaseProps &
  (ChipGroupControlledProps | ChipGroupUncontrolledProps) &
  WithChildren;

/**
 * ChipGroup component
 */
const RootChipGroup = forwardRef<HTMLElement, ChipGroupProps>(
  (props, forwardedRef) => {
    const {
      children,
      expanded: expandedProp,
      defaultExpanded,
      disabled,
      size,
      onExpandedChange,
      maxVisibleChips = 'auto',
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      ...remainingProps
    } = props;

    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);
    const ariaLabelingProps = useAriaLabelingProps(restProps);
    const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);

    const uniqueId = useId();

    const [state, dispatch] = useReducer(chipGroupReducer, {
      chips: {},
    });

    const containerRef = useRef<HTMLDivElement | null>(null);
    const rootRef = useMergeRefs<HTMLElement | null>([
      containerRef,
      forwardedRef,
    ]);

    // Width of the chip group container.
    const { width: containerWidth } = useContainerSize(containerRef);

    const [expanded, setExpanded] = useControllableState({
      value: expandedProp,
      defaultValue: defaultExpanded,
      onChange: onExpandedChange,
    });

    const [allChipsFit, setAllChipsFit] = useState(false);
    const [hiddenChips, setHiddenChips] = useState<string[]>([]);
    const [visibleChips, setVisibleChips] = useState<string[]>([]);

    useEffect(() => {
      const chips = Object.entries(state.chips);
      const chipKeys = chips.map(([uniqueChipKey]) => uniqueChipKey);

      // Reset visibility when there are no chips.
      if (chips.length === 0) {
        setVisibleChips([]);
        return;
      }

      // Cannot calculate visibility when any chip has not been measured yet.
      if (chips.some(([, chip]) => !chip.width)) {
        return;
      }

      // No need to calculate fitting chips if the group is expanded, but all of them have to be set to visible again.
      if (expanded) {
        setHiddenChips([]);
        setVisibleChips(chipKeys);

        if (
          !containerRef.current?.firstChild // ChipGroup may be empty.
        ) {
          setAllChipsFit(true);
          return;
        }

        // Check if the flex container is wrapping. If not, the container has grown
        // enough so all chips fit again, and we can hide the control.
        let wrap = false;
        let child = containerRef.current.firstChild as HTMLElement;

        // requestAnimationFrame ensures the DOM updates are completed before performing the check
        // and prevent issues with displaying the "show less" button.
        const rafId = requestAnimationFrame(() => {
          // Check if any chip is wrapped to the next line.
          do {
            if (child.classList.contains('chip') && child.offsetTop > 0) {
              wrap = true;
            }
            child = child.nextSibling as HTMLElement;
          } while (!wrap && child);
          setAllChipsFit(!wrap);

          // Restore focus to the control button after expansion.
          // There are one or more re-renders in between so need to
          // use setTimeout (macrotask) to defer this until re-renders are finished.
          setTimeout(() => {
            const controlButton =
              containerRef.current?.querySelector<HTMLButtonElement>(
                `button[aria-controls="${uniqueId}"]`,
              );
            if (controlButton) {
              controlButton.focus();
            }
          }, 0);
        });

        return () => {
          cancelAnimationFrame(rafId);
        };
      }

      // Flex gap needs to be considered in the calculation.
      // Either `Spacings.Size6` or `Spacings.Size8`
      const spacing = size === 'condensed' ? 6 : 8;

      /**
       * Iterate over all chips and check, if the chips + flex gap still fit in one line.
       */
      let totalChipsWidth = 0;
      let setHidden = true;
      let _hiddenChipsIndex = chips.length;

      for (const [index, [, value]] of Array.from(chips).entries()) {
        totalChipsWidth += value.width || 0;

        // Whether the chips have enough space in the container. The first chip is always visible.
        const chipFits =
          index === 0 ||
          Math.floor(totalChipsWidth + index * spacing) <=
            Math.round(containerWidth);

        // Whether the chips + control have enough space in the container.
        const chipAndControlFits =
          index === 0 ||
          totalChipsWidth +
            (index + 1) * spacing +
            (state.control?.width || 0) <=
            containerWidth;

        // Whether the chip should be visible.
        // If we are at the last chip and the visibility of the control is determined automatically,
        // we can safely show the chip. In this case, all of them fit in one line and the control will be hidden.
        // If the control is always shown, we only show the chip if there's enough space left to also render the control.
        const visible =
          index === chips.length - 1 &&
          (state.control?.show ?? 'auto') === 'auto'
            ? chipFits
            : chipAndControlFits;

        // Set the index of the first hidden chip for the first chip that doesn't fit anymore.
        if (!visible && setHidden) {
          setHidden = false;

          _hiddenChipsIndex = index;
        }
      }

      // Apply maxVisibleChips limit if specified and valid (non-negative)
      const effectiveHiddenChipsIndex =
        maxVisibleChips !== 'auto' && maxVisibleChips >= 0
          ? Math.min(_hiddenChipsIndex, maxVisibleChips)
          : _hiddenChipsIndex;

      const allFit = effectiveHiddenChipsIndex === chips.length;
      setAllChipsFit(allFit);

      setHiddenChips(chipKeys.slice(effectiveHiddenChipsIndex));
      setVisibleChips(chipKeys.slice(0, effectiveHiddenChipsIndex));
    }, [containerWidth, expanded, size, state, maxVisibleChips, uniqueId]);

    return (
      <ChipGroupContext.Provider
        value={{
          visibleKeys: visibleChips,
          hiddenKeys: hiddenChips,
        }}
      >
        <InternalChipGroupContext.Provider
          value={{
            chipGroupId: uniqueId,
            disabled,
            size,
            expanded,
            allChipsFit,
            dispatch,
            setExpanded,
          }}
        >
          <Flex
            ref={rootRef}
            id={uniqueId}
            className={clsx(
              chipGroupCSS({ size, expanded }),
              consumerClassName,
            )}
            style={consumerStyle}
            data-testid={dataTestId}
            data-dtrum-mask={dataDtrumMask}
            data-dtrum-allow={dataDtrumAllow}
            {...ariaLabelingProps}
            {...behavioralTrackingProps}
            {...forwardProps}
            data-dt-component={COMPONENT_NAME}
          >
            {children}
          </Flex>
        </InternalChipGroupContext.Provider>
      </ChipGroupContext.Provider>
    );
  },
);

(RootChipGroup as typeof RootChipGroup & { displayName: string }).displayName =
  COMPONENT_NAME;

/**
 * Use the `ChipGroup` component to group multiple `Chip`s in an expandable container.
 * @public
 */
export const ChipGroup = Object.assign(RootChipGroup, {
  Control,
  HiddenCount,
});
